import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DialogContent,
  Typography,
  Grid,
  DialogActions,
  Button,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { filter, range, sortBy } from 'lodash-es';
import { FullScreenDialog } from '../../components/FullScreenDialog';
import { selectors } from '../../store/dux/soyrx-dux';
import {
  actions,
  selectors as vpiSelector,
} from '../../store/dux/product-placement-vpi-dux';
import { DialogTitle } from '../../components/DialogTitle';
import AssignProductRow from './AssignProductRow';
import { useAlerts } from '../../hooks/use-alerts';
import { Spinner } from '../../components/Spinner';

export function VPIAssignProductsModal(props) {
  const { close, growerId } = props;
  const dispatch = useDispatch();
  const selectedSoyFields = useSelector(selectors.selectedSoyFields);
  const saving = useSelector(vpiSelector.saving);
  const hasFailed = useSelector(vpiSelector.hasFailed);
  const saveComplete = useSelector(vpiSelector.saveComplete);
  const isAssignmentValid = useSelector(vpiSelector.isAssignmentValid);
  const productAssignments = useSelector(vpiSelector.productAssignments);
  const loading = useSelector(vpiSelector.loading);

  const { showError } = useAlerts();

  useEffect(() => {
    if (hasFailed) {
      showError(hasFailed);
    }
  }, [hasFailed, showError]);

  useEffect(() => {
    if (saveComplete) {
      close();
    }
  }, [saveComplete, close]);

  useEffect(() => {
    if (selectedSoyFields.length) {
      dispatch(
        actions.loadAssignments(
          growerId,
          selectedSoyFields.map((f) => f.aaManagedId),
        ),
      );
    }
  }, [growerId, selectedSoyFields, dispatch]);

  async function saveFieldAssignments() {
    await dispatch(actions.createVPISummary(growerId));
  }

  function getFieldAssignments(field) {
    return filter(productAssignments, { aaid: field.aaManagedId });
  }

  const headerTextStyle = { fontWeight: '500' };

  const headerStyle = {
    bgcolor: (theme) => theme.palette.background.main,
    height: 60,
    boxShadow: '0px -1px 0px 0px #C2C2C2 inset',
  };

  return (
    <FullScreenDialog onCancel={close} keepMounted open sx={{ top: 96 }}>
      <DialogTitle
        title="Assign Products"
        onClose={close}
        sx={{ boxShadow: 4, mb: 8 }}
      />
      <DialogContent>
        <Grid
          container
          display={'flex'}
          alignItems="center"
          justify="center"
          sx={{ mt: 2, width: '80%', mx: '10%' }}
        >
          <Grid
            xs={3}
            item
            align="left"
            display={'flex'}
            alignItems="center"
            sx={{ ...headerStyle, pl: 1 }}
          >
            <Typography sx={headerTextStyle}>Field Name</Typography>
          </Grid>
          {range(0, 3).map((index) => (
            <Grid item xs={3} key={index}>
              <Grid container>
                <Grid
                  xs={7}
                  item
                  align="left"
                  display={'flex'}
                  alignItems="center"
                  sx={headerStyle}
                >
                  <Typography sx={headerTextStyle}>
                    Product {index + 1}
                  </Typography>
                </Grid>
                <Grid
                  xs={3}
                  item
                  sx={headerStyle}
                  display={'flex'}
                  alignItems="center"
                  align="left"
                >
                  <Typography sx={headerTextStyle}>Percent</Typography>
                </Grid>
                <Grid xs={2} item sx={headerStyle}>
                  &nbsp;
                </Grid>
              </Grid>
            </Grid>
          ))}
          {loading ? (
            <Spinner />
          ) : (
            sortBy(selectedSoyFields, 'fieldName').map((field) => (
              <Grid
                container
                alignItems={'center'}
                key={field.aaManagedId}
                sx={{
                  py: 2,
                  boxShadow: '0px -1px 0px 0px #C2C2C2 inset',
                }}
              >
                <AssignProductRow
                  field={field}
                  productAssignments={getFieldAssignments(field)}
                  crop={'soy'}
                />
              </Grid>
            ))
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3, boxShadow: '0px 1px 5px 0px #00000033' }}>
        <Button sx={{ mr: 1 }} onClick={close} variant="contained" color="dark">
          Cancel
        </Button>
        <LoadingButton
          onClick={saveFieldAssignments}
          variant="contained"
          color="primary"
          loading={saving}
          disabled={!isAssignmentValid}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </FullScreenDialog>
  );
}
